import React from 'react';
import { Link } from 'react-router-dom';
import { getSeriesNeighbors, getSeriesPath } from '../lib/format';

const isSamePost = (part, postId) => String(part.postId) === String(postId);

// Series box above an article: the series name (linking to the series page
// for writer-created series) and one chip per part, titled with the part's
// headline.
function SeriesNavigator({ series, postId }) {
    if (!series || !Array.isArray(series.parts) || series.parts.length === 0) {
        return null;
    }

    const { index } = getSeriesNeighbors(series, postId);
    const currentPart = index >= 0 ? series.parts[index] : null;
    // Writer series are numbered 1..N; detected numbers can have gaps.
    let position = '';
    if (currentPart) {
        position = series.explicit
            ? ` · Part ${currentPart.part} of ${series.parts.length}`
            : ` · Part ${currentPart.part}`;
    }

    return (
        <div className="series-navigator">
            <div className="series-navigator-label">{`Series${position}`}</div>
            <div className="series-navigator-title">
                {series.slug ? (
                    <Link to={getSeriesPath(series)}>{series.name}</Link>
                ) : series.name}
            </div>
            <div className="series-navigator-parts">
                {series.parts.map((part) => {
                    const current = isSamePost(part, postId);
                    return (
                        <Link
                            key={part.postId}
                            className={`series-part-link${current ? ' current' : ''}`}
                            to={`/posts/${part.postId}`}
                            title={part.title}
                            aria-current={current ? 'page' : undefined}
                        >
                            {`Part ${part.part}`}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}

// "← Previous in series / Next in series →" at the end of an article.
export function SeriesPartNav({ series, postId }) {
    const { previous, next } = getSeriesNeighbors(series, postId);
    if (!previous && !next) {
        return null;
    }

    return (
        <nav className="series-part-nav" aria-label="Series navigation">
            <div className="series-part-nav-heading">
                <span className="eyebrow">Series</span>
                {series.slug ? (
                    <Link className="series-part-nav-name" to={getSeriesPath(series)}>{series.name}</Link>
                ) : (
                    <span className="series-part-nav-name">{series.name}</span>
                )}
            </div>
            <div className="series-part-nav-links">
                <div className="series-part-nav-prev">
                    {previous && (
                        <Link to={`/posts/${previous.postId}`}>
                            <span className="eyebrow">← Previous in series</span>
                            <span className="series-part-nav-title">
                                {`Part ${previous.part} · ${previous.title}`}
                            </span>
                        </Link>
                    )}
                </div>
                <div className="series-part-nav-next">
                    {next && (
                        <Link to={`/posts/${next.postId}`}>
                            <span className="eyebrow">Next in series →</span>
                            <span className="series-part-nav-title">
                                {`Part ${next.part} · ${next.title}`}
                            </span>
                        </Link>
                    )}
                </div>
            </div>
        </nav>
    );
}

export default SeriesNavigator;
